<script>
import axios from "axios";

export default {
  name: "InterviewScheduler",
  data() {
    return {
      // URL params (encoded)
      encodedAgent: null,
      encodedDept: null,

      agentInfo: { firstName: '', lastName: '', profileIMG: '' },
      bookedSlots: [],

      // Calendar state
      currentMonthDate: null,
      calendarWeeks: [],
      selectedDate: null,
      selectedTime: null,
      availableTimes: [],

      scheduleConfig: {}, // {monday: ["09:00","14:00"], ...} from API

      // Form
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },

      loading: false,
      submitted: false,
      errorMsg: '',
    };
  },

  computed: {
    selectedDateTime() {
      if (!this.selectedDate || !this.selectedTime) return null;
      return `${this.selectedDate}T${this.selectedTime}:00`;
    },
    confirmText() {
      if (!this.selectedDate || !this.selectedTime) return '';
      const d = new Date(`${this.selectedDate}T${this.selectedTime}`);
      return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
        + ' at '
        + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    },
    currentMonthLabel() {
      if (!this.currentMonthDate) return '';
      return this.currentMonthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    isPrevMonthDisabled() {
      if (!this.currentMonthDate) return true;
      const today = new Date();
      return this.currentMonthDate.getFullYear() === today.getFullYear()
          && this.currentMonthDate.getMonth() === today.getMonth();
    },
  },

  methods: {
    async loadInfo() {
      const params = new URLSearchParams(window.location.search);
      this.encodedAgent = params.get('a') || '';
      this.encodedDept = params.get('d') || '';

      try {
        const res = await axios.get('/api/public/interview-info', {
          params: { a: this.encodedAgent, d: this.encodedDept },
        });
        if (res.data.agent) this.agentInfo = res.data.agent;
        this.bookedSlots = res.data.bookedSlots || [];
        this.scheduleConfig = res.data.schedule || {};
      } catch (e) {
        console.error('[InterviewScheduler] API error:', e);
      }

      this.buildCalendar();
    },

    dayName(jsDay) {
      // JS getDay(): 0=Sun,1=Mon,...,6=Sat  →  schedule key
      return ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][jsDay];
    },

    hasSchedule(dateStr) {
      const slots = this.scheduleConfig[this.dayName(new Date(dateStr + 'T12:00:00').getDay())] || [];
      // No config at all → treat every Mon-Fri as available (fallback)
      const noConfig = Object.values(this.scheduleConfig).every(s => !s || s.length === 0);
      if (noConfig) return new Date(dateStr + 'T12:00:00').getDay() !== 0 && new Date(dateStr + 'T12:00:00').getDay() !== 6;
      return slots.length > 0;
    },

    buildCalendar() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];

      if (!this.currentMonthDate) {
        this.currentMonthDate = new Date(today.getFullYear(), today.getMonth(), 1);
      }

      const year = this.currentMonthDate.getFullYear();
      const month = this.currentMonthDate.getMonth();
      const lastDay = new Date(year, month + 1, 0);

      // Start from Monday before/on the 1st of the month
      let start = new Date(year, month, 1);
      const dow = start.getDay(); // 0=Sun
      const daysBack = dow === 0 ? 6 : dow - 1;
      start.setDate(start.getDate() - daysBack);

      const weeks = [];
      let cur = new Date(start);

      while (cur <= lastDay || weeks.length < 5) {
        if (cur > lastDay && cur.getDay() === 1) break;
        const week = [];
        for (let i = 0; i < 7; i++) {
          const dateStr = cur.toISOString().split('T')[0];
          const isCurrentMonth = cur.getMonth() === month;
          const isPast = cur < today;
          week.push({
            date: dateStr,
            label: cur.getDate(),
            isCurrentMonth,
            isPast,
            active: isCurrentMonth && !isPast && this.hasSchedule(dateStr),
            isToday: dateStr === todayStr,
          });
          cur.setDate(cur.getDate() + 1);
        }
        weeks.push(week);
      }

      this.calendarWeeks = weeks;
    },

    prevMonth() {
      this.currentMonthDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() - 1, 1);
      this.buildCalendar();
    },

    nextMonth() {
      this.currentMonthDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() + 1, 1);
      this.buildCalendar();
    },

    selectDate(day) {
      this.selectedDate = day.date;
      this.selectedTime = null;
      this.buildTimeSlots(day.date);
    },

    buildTimeSlots(dateStr) {
      const dayKey = this.dayName(new Date(dateStr + 'T12:00:00').getDay());
      const configSlots = this.scheduleConfig[dayKey] || [];

      // Fallback: no config → 9am-5pm every 30min
      const noConfig = Object.values(this.scheduleConfig).every(s => !s || s.length === 0);
      const sourceTimes = noConfig
        ? (() => {
            const t = [];
            for (let h = 9; h < 17; h++) for (let m of [0, 30])
              t.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
            return t;
          })()
        : configSlots;

      this.availableTimes = sourceTimes.map(timeStr => {
        const [h, m] = timeStr.split(':').map(Number);
        return { time: timeStr, label: this.formatTime(h, m), booked: false };
      });
    },

    formatTime(h, m) {
      const period = h < 12 ? 'AM' : 'PM';
      const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    },

    async submitForm() {
      if (!this.selectedDateTime) {
        this.errorMsg = 'Please select a date and time.';
        return;
      }
      if (!this.form.firstName || !this.form.email || !this.form.phone) {
        this.errorMsg = 'Please fill in all required fields.';
        return;
      }

      this.errorMsg = '';
      this.loading = true;

      try {
        const res = await axios.post('/api/public/schedule-interview', {
          a: this.encodedAgent,
          d: this.encodedDept,
          firstName: this.form.firstName,
          lastName: this.form.lastName || 'N/A',
          email: this.form.email,
          phoneNumber: this.form.phone,
          interviewDate: this.selectedDateTime,
        });

        if (res.data === true) {
          this.submitted = true;
        } else {
          this.errorMsg = 'Something went wrong. Please try again.';
        }
      } catch (e) {
        this.errorMsg = 'Something went wrong. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    isSelected(dayDate) {
      return this.selectedDate === dayDate;
    },

    isToday(dayDate) {
      return dayDate === new Date().toISOString().split('T')[0];
    },
  },

  mounted() {
    this.loadInfo();

    window.addEventListener('scroll', () => {
      const navbar = document.getElementById('navbar');
      if (navbar) {
        if (document.body.scrollTop >= 50 || document.documentElement.scrollTop >= 50) {
          navbar.classList.add('is-sticky');
        } else {
          navbar.classList.remove('is-sticky');
        }
      }
    });
  },
};
</script>

<template>
  <div class="layout-wrapper landing">

    <!-- Navbar -->
    <nav class="navbar navbar-expand-lg navbar-landing fixed-top bg-white shadow-sm" id="navbar">
      <div class="container">
        <a class="navbar-brand" href="/">
          <img src="@/assets/images/logo-dark.png" class="card-logo card-logo-dark" alt="logo" height="22">
        </a>
      </div>
    </nav>
    <!-- end navbar -->

    <!-- Hero -->
    <section class="section pb-0 hero-section" style="background: linear-gradient(135deg, #e8f5eb 0%, #ffffff 100%); padding-top: 100px;">
      <div class="container pb-5">
        <div class="row justify-content-center text-center mb-5">
          <div class="col-lg-7">
            <img src="@/assets/images/logo-dark.png" alt="DMB Insurance Group" height="40" class="mb-4">
            <h1 class="display-5 fw-bold text-dark mb-3">
              Schedule Your <span class="text-primary">Interview</span>
            </h1>
            <p class="lead text-muted">
              Pick a date and time that works best for you. We'll confirm your appointment right away.
            </p>
          </div>
        </div>

        <!-- Main card -->
        <div class="row justify-content-center">
          <div class="col-lg-9">
            <div class="card shadow-lg border-0 rounded-4 overflow-hidden border-top border-5 border-primary">

              <!-- Agent banner -->
              <div v-if="agentInfo.firstName" class="bg-primary-subtle py-3 px-4 border-bottom">
                <div class="d-flex align-items-center gap-3 justify-content-center">
                  <div v-if="agentInfo.profileIMG" class="avatar-sm">
                    <img :src="agentInfo.profileIMG" class="avatar-title rounded-circle shadow-sm" alt="agent">
                  </div>
                  <div v-else class="avatar-sm">
                    <div class="avatar-title bg-primary rounded-circle text-white fw-bold shadow-sm">
                      {{ agentInfo.firstName[0] }}{{ agentInfo.lastName ? agentInfo.lastName[0] : '' }}
                    </div>
                  </div>
                  <div class="text-start">
                    <p class="mb-0 text-primary fw-bold fs-11 text-uppercase">Your Interviewer</p>
                    <h6 class="mb-0 fw-bold text-dark">{{ agentInfo.firstName }} {{ agentInfo.lastName }}</h6>
                  </div>
                </div>
              </div>

              <!-- Success state -->
              <div v-if="submitted" class="card-body text-center py-5">
                <div class="avatar-lg mx-auto mb-4">
                  <div class="avatar-title bg-success-subtle text-success rounded-circle" style="font-size: 2rem;">
                    <i class="ri-checkbox-circle-fill"></i>
                  </div>
                </div>
                <h4 class="fw-bold text-dark mb-2">You're all set!</h4>
                <p class="text-muted mb-1">Your interview has been scheduled for:</p>
                <p class="fw-semibold text-primary fs-16">{{ confirmText }}</p>
                <p class="text-muted fs-13 mt-3">We'll reach out to confirm your appointment. Check your email.</p>
              </div>

              <!-- Scheduler -->
              <div v-else class="card-body p-4">
                <div class="row g-4">

                  <!-- Left: Calendar -->
                  <div class="col-lg-7">
                    <h6 class="fw-bold text-dark mb-3">
                      <i class="ri-calendar-line me-1 text-primary"></i> Select a Date
                    </h6>

                    <!-- Month navigation -->
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <button type="button" class="btn btn-sm btn-light rounded-circle cal-nav-btn"
                              @click="prevMonth" :disabled="isPrevMonthDisabled">
                        <i class="ri-arrow-left-s-line"></i>
                      </button>
                      <span class="fw-semibold text-dark fs-14">{{ currentMonthLabel }}</span>
                      <button type="button" class="btn btn-sm btn-light rounded-circle cal-nav-btn" @click="nextMonth">
                        <i class="ri-arrow-right-s-line"></i>
                      </button>
                    </div>

                    <!-- Day-of-week headers -->
                    <div class="cal-grid mb-1">
                      <div v-for="h in ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']" :key="h"
                           class="text-center text-muted fw-semibold" style="font-size:11px; padding:4px 0;">
                        {{ h }}
                      </div>
                    </div>

                    <!-- Weeks -->
                    <div v-for="(week, wi) in calendarWeeks" :key="wi" class="cal-grid mb-1">
                      <button
                        v-for="day in week"
                        :key="day.date"
                        type="button"
                        class="cal-day-btn"
                        :class="{
                          'cal-selected': isSelected(day.date),
                          'cal-today': day.isToday && !isSelected(day.date),
                          'cal-active': day.active && !isSelected(day.date) && !day.isToday,
                          'cal-inactive': !day.active || !day.isCurrentMonth,
                        }"
                        :disabled="!day.active"
                        @click="day.active && selectDate(day)"
                      >
                        {{ day.isCurrentMonth ? day.label : '' }}
                      </button>
                    </div>

                    <!-- Time slots -->
                    <div v-if="selectedDate" class="mt-4">
                      <h6 class="fw-bold text-dark mb-3">
                        <i class="ri-time-line me-1 text-primary"></i> Select a Time
                      </h6>
                      <div class="d-grid" style="grid-template-columns: repeat(4, 1fr); gap: 6px;">
                        <button
                          v-for="slot in availableTimes"
                          :key="slot.time"
                          type="button"
                          class="btn btn-sm rounded-3 py-2"
                          :class="{
                            'btn-primary text-white': selectedTime === slot.time,
                            'btn-light border': selectedTime !== slot.time && !slot.booked,
                            'btn-light border text-muted opacity-50': slot.booked,
                          }"
                          :disabled="slot.booked"
                          @click="selectedTime = slot.time"
                        >
                          {{ slot.label }}
                        </button>
                      </div>
                    </div>

                    <div v-if="!selectedDate" class="mt-4 text-center text-muted py-4 border rounded-3 bg-light">
                      <i class="ri-calendar-line d-block mb-2 fs-20 text-primary"></i>
                      <span class="fs-13">Choose a date to see available times</span>
                    </div>
                  </div>

                  <!-- Right: Form -->
                  <div class="col-lg-5">
                    <h6 class="fw-bold text-dark mb-3">
                      <i class="ri-user-line me-1 text-primary"></i> Your Information
                    </h6>

                    <!-- Selected slot summary -->
                    <div v-if="confirmText" class="alert alert-primary py-2 px-3 rounded-3 fs-13 mb-3">
                      <i class="ri-calendar-check-line me-1"></i> <strong>{{ confirmText }}</strong>
                    </div>

                    <form @submit.prevent="submitForm">
                      <div class="mb-3">
                        <label class="form-label text-muted fw-semibold fs-13">First Name <span class="text-danger">*</span></label>
                        <input type="text" class="form-control bg-light border-light" v-model="form.firstName" placeholder="John" required>
                      </div>
                      <div class="mb-3">
                        <label class="form-label text-muted fw-semibold fs-13">Last Name</label>
                        <input type="text" class="form-control bg-light border-light" v-model="form.lastName" placeholder="Doe">
                      </div>
                      <div class="mb-3">
                        <label class="form-label text-muted fw-semibold fs-13">Email <span class="text-danger">*</span></label>
                        <input type="email" class="form-control bg-light border-light" v-model="form.email" placeholder="john@example.com" required>
                      </div>
                      <div class="mb-4">
                        <label class="form-label text-muted fw-semibold fs-13">Phone <span class="text-danger">*</span></label>
                        <input type="tel" class="form-control bg-light border-light" v-model="form.phone" placeholder="(555) 123-4567" required>
                      </div>

                      <div v-if="errorMsg" class="alert alert-danger py-2 px-3 fs-13 rounded-3">
                        <i class="ri-error-warning-line me-1"></i> {{ errorMsg }}
                      </div>

                      <button
                        type="submit"
                        class="btn btn-primary btn-lg w-100 rounded-pill fw-bold shadow-sm"
                        :disabled="loading || !selectedDateTime"
                      >
                        <span v-if="loading">
                          <i class="ri-loader-4-line me-1 spin-icon"></i> Scheduling...
                        </span>
                        <span v-else>
                          <i class="ri-calendar-check-line me-1"></i> Confirm Interview
                        </span>
                      </button>

                      <div v-if="loading" class="text-center mt-3">
                        <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span class="text-muted fs-13">Please wait, submitting your appointment...</span>
                      </div>

                      <p class="text-center text-muted fs-12 mt-3 mb-0">
                        <i class="ri-lock-2-line me-1"></i> Your info is safe with us
                      </p>
                    </form>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="py-4 bg-white border-top mt-5">
      <div class="container text-center">
        <img src="@/assets/images/logo-dark.png" alt="DMB Insurance Group" height="20" class="mb-2 d-block mx-auto opacity-75">
        <p class="text-muted mb-0 fs-13">&copy; {{ new Date().getFullYear() }} DMB Insurance Group. All rights reserved.</p>
      </div>
    </footer>

  </div>
</template>

<style scoped>
.layout-wrapper {
  --bs-primary: #006414;
  --bs-primary-rgb: 0, 100, 20;
  --bs-primary-bg-subtle: #e8f5eb;
  --bs-primary-border-subtle: #b3d9bc;
  --bs-primary-text-emphasis: #003d0c;
  --bs-link-color: #006414;
  --bs-link-hover-color: #004d0f;
}

.btn-primary {
  --bs-btn-color: #fff;
  --bs-btn-bg: #006414;
  --bs-btn-border-color: #006414;
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: #005211;
  --bs-btn-hover-border-color: #004d0f;
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: #004d0f;
  --bs-btn-disabled-color: #fff;
  --bs-btn-disabled-bg: #006414;
  --bs-btn-disabled-border-color: #006414;
}

.btn-outline-primary {
  --bs-btn-color: #006414;
  --bs-btn-border-color: #006414;
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: #006414;
  --bs-btn-hover-border-color: #006414;
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: #006414;
}

.alert-primary {
  --bs-alert-color: #003d0c;
  --bs-alert-bg: #e8f5eb;
  --bs-alert-border-color: #b3d9bc;
}

.text-primary { color: #006414 !important; }
.bg-primary { background-color: #006414 !important; }
.border-primary { border-color: #006414 !important; }
.bg-primary-subtle { background-color: #e8f5eb !important; }

/* Monthly calendar grid */
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}
.cal-day-btn {
  display: flex; align-items: center; justify-content: center;
  height: 36px; width: 100%;
  border: none; border-radius: 8px;
  font-size: 13px; font-weight: 500;
  cursor: pointer; background: transparent; color: #212529;
  transition: background .15s, color .15s;
}
.cal-day-btn:disabled { cursor: default; }
.cal-active { background: #f0f0f0; }
.cal-active:hover { background: #e8f5eb; color: #006414; }
.cal-today { background: #e8f5eb; color: #006414; font-weight: 700; border: 2px solid #006414; }
.cal-selected { background: #006414 !important; color: #fff !important; font-weight: 700; }
.cal-inactive { color: #ccc; }
.cal-nav-btn { width: 32px; height: 32px; padding: 0; display: flex; align-items: center; justify-content: center; }

@keyframes spin { to { transform: rotate(360deg); } }
.spin-icon { display: inline-block; animation: spin .8s linear infinite; }
</style>
